import { useLocalStorage } from '@uidotdev/usehooks';
import { forgetRowHeight } from 'components/tables/data-table/rememberedRowHeight';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { isTourMode } from 'tour/tourMode';
import { ColumnLayoutEntry, ColumnLayoutStore } from './types';

/**
 * Where a list's column layouts live. Classic and every list outside saved views use the default,
 * 'list-columns'. A list of saved views in the new design keeps its layouts under a key carrying the
 * employee's tenant and id, and reads an entry it has not written yet as `fallbacks` gives it: a saved
 * view's working layout as the view's stored one, and a layout never changed in the new design as the
 * classic one.
 */
export type ColumnLayoutStorage = {
    storageKey: string;
    fallbacks?: Record<string, ColumnLayoutEntry | undefined>;
};

export const CLASSIC_COLUMN_STORAGE: ColumnLayoutStorage = { storageKey: 'list-columns' };

/** For the DataTable inside a list, which reads its widths through its own instance of the hook. */
export const ColumnLayoutStorageContext = createContext<ColumnLayoutStorage | undefined>(undefined);

/** The id a saved view's working column layout is kept under. */
export const viewColumnConfigId = (columnConfigId: string, viewId: number) => `${columnConfigId}@view-${viewId}`;

/**
 * localStorage layer for the managed column layout, mirroring
 * `useFilterPersistence` but keyed by `columnConfigId` instead of the normalized URL path —
 * the six invoice tabs are distinct column sets behind one route family — and stored under
 * a separate key (see {@link ColumnLayoutStorage}).
 *
 * Unknown colIds (column removed/renamed in a later deploy) are ignored at read time by the
 * consumers; a colId absent from a `hidden` snapshot renders visible, and width ratios are
 * deltas, so entries degrade gracefully without a version field.
 *
 * Multiple hook instances on the same key stay in sync within the tab: `useLocalStorage`
 * dispatches a storage event on every write. As with `useFilterPersistence` (see its
 * batching caveat), make exactly ONE save call per logical commit (resize-end, visibility
 * toggle, reset) — a second synchronous call would build on the pre-write snapshot.
 */
export const useColumnLayoutPersistence = (columnConfigId: string | undefined, storage?: ColumnLayoutStorage) => {
    const contextStorage = useContext(ColumnLayoutStorageContext);
    const { storageKey, fallbacks } = storage ?? contextStorage ?? CLASSIC_COLUMN_STORAGE;
    const [store, setStore] = useLocalStorage<ColumnLayoutStore>(storageKey, {});
    const fallback = columnConfigId ? fallbacks?.[columnConfigId] : undefined;

    // useLocalStorage re-parses the raw string every render, so the store object's identity
    // churns. Re-memo the entry by content: downstream effects (setHiddenColumns sync, width
    // resolution) depend on its identity and must not re-fire on unrelated renders.
    const rawEntry = columnConfigId ? (store?.[columnConfigId] ?? fallback) : undefined;
    const entryJson = rawEntry !== undefined ? JSON.stringify(rawEntry) : undefined;
    const entry = useMemo<ColumnLayoutEntry | undefined>(
        () => (entryJson !== undefined ? (JSON.parse(entryJson) as ColumnLayoutEntry) : undefined),
        [entryJson]
    );

    const updateEntry = useCallback(
        (update: (current: ColumnLayoutEntry) => ColumnLayoutEntry) => {
            if (!columnConfigId || isTourMode()) return;
            setStore((prev) => {
                const safePrev = prev ?? {};
                return { ...safePrev, [columnConfigId]: update(safePrev[columnConfigId] ?? fallback ?? {}) };
            });
        },
        [columnConfigId, setStore, fallback]
    );

    const saveWidthRatio = useCallback(
        (colId: string, ratio: number) => {
            updateEntry((current) => ({ ...current, widthRatios: { ...current.widthRatios, [colId]: ratio } }));
        },
        [updateEntry]
    );

    const removeWidthRatio = useCallback(
        (colId: string) => {
            updateEntry((current) => {
                const { [colId]: _removed, ...widthRatios } = current.widthRatios ?? {};
                return { ...current, widthRatios };
            });
        },
        [updateEntry]
    );

    const saveHidden = useCallback(
        (hidden: string[], knownIds: string[]) => {
            updateEntry((current) => ({ ...current, hidden, knownIds }));
        },
        [updateEntry]
    );

    /** Persist the full column-order snapshot. Mirrors `saveHidden`: one write per
     *  logical commit (a drag-reorder drop). Read-time reconciliation handles stale ids. */
    const saveOrder = useCallback(
        (order: string[]) => {
            updateEntry((current) => ({ ...current, order }));
        },
        [updateEntry]
    );

    /** Drop the whole entry for this table: widths and visibility fall back to the
     *  column-def defaults, or to the layout `fallbacks` gives, and the skeleton height those columns
     *  produced is forgotten with them. The "Reset columns" escape hatch. */
    const reset = useCallback(() => {
        if (!columnConfigId || isTourMode()) return;
        forgetRowHeight(columnConfigId);
        setStore((prev) => {
            const { [columnConfigId]: _removed, ...rest } = prev ?? {};
            return rest;
        });
    }, [columnConfigId, setStore]);

    return useMemo(
        () => ({ entry, saveWidthRatio, removeWidthRatio, saveHidden, saveOrder, reset }),
        [entry, saveWidthRatio, removeWidthRatio, saveHidden, saveOrder, reset]
    );
};
